import { useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"
import nProgress from "nprogress"

import { lang } from "config"
import { useAuth } from "contexts/auth-context"
import { getCurrentEntity } from "helpers/general-helper"
import { defaultUomForEntity, loadMasterUomFactors, masterUomFactor, normalizeUomCode } from "helpers/uom-helper"
import Input from "components/forms/input"
import Label from "components/forms/label"
import Description from "components/forms/description"
import ErrorMessage from "components/forms/error-message"

function AsyncProductUnitPicker({
  disabled,
  outlet_group_id,
  outlet_id,
  value,
  onChange,
}) {
  const inventoryApi = process.env.REACT_APP_INVENTORY_API_URL
  const perPage = 30

  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const debounceRef = useRef()

  const buildUrl = (params) => {
    const url = new URL(`${inventoryApi}/ps_list`)
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== "") {
        url.searchParams.set(key, val)
      }
    })
    return url.toString()
  }

  const fetchPage = async (targetPage = 1, append = false) => {
    if (!inventoryApi || !outlet_id) return

    setLoading(true)
    try {
      const response = await fetch(
        buildUrl({
          page: targetPage,
          per_page: perPage,
          search: query,
          outlet_group_id,
          outlet_id,
          only_in_stock: true,
          with_attributes: false,
        })
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const json = await response.json()
      const rows = Array.isArray(json?.data) ? json.data : []

      setItems((prev) => (append ? [...prev, ...rows] : rows))
      setHasMore(Boolean(json?.meta?.next_page_url) || rows.length === perPage)
      setPage(targetPage)
    } catch {
      toast.error("Gagal mengambil daftar produk.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!outlet_id) {
      setItems([])
      return
    }

    fetchPage(1, false)
  }, [outlet_id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchPage(1, false)
    }, 300)

    return () => clearTimeout(debounceRef.current)
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  const onScroll = (event) => {
    const el = event.currentTarget
    if (!loading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      fetchPage(page + 1, true)
    }
  }

  if (disabled || !outlet_id) {
    return <Input disabled value="Pilih outlet asal dulu" id="product_unit_picker_disabled" />
  }

  return (
    <div className="space-y-2">
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Cari SKU / Nama / Brand / Model..."
      />

      <div className="h-72 overflow-auto border rounded-xl" onScroll={onScroll}>
        {items.map((row) => {
          const id = row.product_unit_id ?? row.id
          const name = row.product_unit_long_name ?? row.product_unit ?? row.name ?? "-"
          const sku = row.sku ?? "-"
          const brand = row.product_brand ?? row.brand ?? "-"
          const stock = Number(row.current_stock ?? row.stock ?? 0)
          const isSelected = String(value?.product_unit_id || "") === String(id)

          return (
            <button
              key={id}
              type="button"
              className={`w-full px-3 py-2 text-left border-b last:border-b-0 hover:bg-neutral-50 ${isSelected ? "bg-neutral-100" : ""}`}
              onClick={() =>
                onChange({
                  product_unit_id: String(id),
                  product_unit_name: name,
                  sku,
                  brand,
                  available_stock: stock,
                })
              }
            >
              <div className="text-sm font-medium text-neutral-900">{name}</div>
              <div className="text-xs text-neutral-500">
                {sku} • {brand}
              </div>
              <div className="mt-1 text-[11px] text-green-700">Stok: {stock}</div>
            </button>
          )
        })}

        {loading && <div className="p-3 text-xs text-neutral-500">Memuat...</div>}
        {!loading && items.length === 0 && <div className="p-3 text-xs text-neutral-500">Tidak ada hasil</div>}
      </div>
    </div>
  )
}

export default function Form({ data, errors = {}, onSubmit }) {
  const genericApi = process.env.REACT_APP_GENERIC_API_URL
  const { isSuper, currentBranch, canFilterAllOutlets } = useAuth()
  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya

  const [fromOutletId, setFromOutletId] = useState(
    isBranchLocked ? String(currentBranch.id) : (data?.from_outlet_id ?? "")
  )
  const [toOutletId, setToOutletId] = useState(data?.to_outlet_id ?? "")
  const [deliveryOrderNo, setDeliveryOrderNo] = useState(data?.delivery_order_no ?? "")
  const [branches, setBranches] = useState([])
  const [loadingBranches, setLoadingBranches] = useState(false)

  // 290726 (permintaan user): pilihan SATUAN untuk transfer stok — pola sama dgn
  // menu Tambah Pembelian. Default ikut ENTITAS user (VISION→PASANG, ATALLA→LUSIN).
  //
  // CATATAN PENTING: `qty` di state & payload TETAP dalam PCS (tidak berubah dari
  // sebelumnya) — satuan hanya memengaruhi cara INPUT & TAMPILAN. Dengan begitu
  // validasi stok, submit, dan hydrate data lama tidak perlu ikut berubah.
  const UOM_FALLBACK = ["PCS", "LUSIN", "PASANG"]
  // 290726: default entitas diisi SINKRON saat mount (lihat ho-order/form).
  const [globalUom, setGlobalUom] = useState(() => defaultUomForEntity(getCurrentEntity(), null))
  const [satuanOptions, setSatuanOptions] = useState(null)

  useEffect(() => {
    let cancelled = false
    loadMasterUomFactors().then((names) => {
      if (cancelled) return
      const opts = names.length ? names : [...UOM_FALLBACK]
      setSatuanOptions(opts)
      // Hanya utk transfer BARU; saat edit, qty tersimpan (pcs) tetap ditampilkan
      // apa adanya dalam satuan terpilih.
      if (!data) setGlobalUom((prev) => normalizeUomCode(prev) || prev)
    })
    return () => { cancelled = true }
  }, [])

  // pcs -> satuan tampilan (maks 2 desimal, buang nol di belakang)
  const toUomQty = (pcs) => {
    const v = Number(pcs || 0) / masterUomFactor(globalUom)
    return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100)
  }
  // satuan input -> pcs (dibulatkan; server hanya menerima bilangan bulat pcs)
  const toPcsQty = (qtyUom) => Math.round(Number(qtyUom || 0) * masterUomFactor(globalUom))

  const [draftProduct, setDraftProduct] = useState(null)
  const [draftQty, setDraftQty] = useState("")
  const [items, setItems] = useState(() => {
    const initialItems = Array.isArray(data?.items) ? data.items : []
    return initialItems.map((item, index) => ({
      line_key: item.line_key || `existing-${index + 1}`,
      product_unit_id: String(item.product_unit_id),
      product_unit_name: item.product_unit_name || item.name || item.product_unit || item.product_unit_id,
      qty: Number(item.qty ?? item.qty_requested ?? 0),
      available_stock: Number(item.available_stock ?? item.current_stock ?? 0),
      sku: item.sku || "-",
      brand: item.brand || item.product_brand || "-",
    }))
  })

  const fetchBranches = async () => {
    setLoadingBranches(true)
    try {
      // paginate=false: ambil SEMUA outlet. Tanpa ini /branch mengembalikan hanya
      // 15 outlet terbaru (latest 15), sehingga outlet lama — termasuk HO (id kecil,
      // dibuat paling awal) — tidak pernah muncul di dropdown Outlet Asal/Tujuan.
      const response = await axios.get(`${genericApi}/branch`, { params: { paginate: false } })
      const body = response?.data?.data ?? response?.data?.branches ?? response?.data ?? []
      const list = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : [])
      setBranches(list)
    } catch {
      toast.error("Gagal memuat outlet")
    } finally {
      setLoadingBranches(false)
    }
  }

  useEffect(() => {
    fetchBranches()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Kosongkan item saat outlet asal DIGANTI, tapi JANGAN pada mount awal — kalau tidak,
  // item draft yang baru saja di-hydrate saat "Lanjutkan" akan langsung terhapus.
  const skipFromOutletReset = useRef(true)
  useEffect(() => {
    if (skipFromOutletReset.current) {
      skipFromOutletReset.current = false
      return
    }
    setItems([])
    setDraftProduct(null)
    setDraftQty("")
  }, [fromOutletId])

  const branchOptionLabel = (branch) => branch?.name ?? branch?.branch_name ?? branch?.code ?? branch?.id ?? ""
  const branchOptionValue = (branch) => branch?.id ?? branch?.branch_id ?? branch?.code ?? ""

  const findBranchNameById = (id) => {
    const branch = branches.find((row) => String(branchOptionValue(row)) === String(id))
    return branch ? branchOptionLabel(branch) : `OUTLET ${id}`
  }

  const totalQty = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.qty || 0), 0),
    [items]
  )

  const addDraftProduct = () => {
    if (!draftProduct?.product_unit_id) {
      toast.error("Pilih produk terlebih dahulu")
      return
    }

    // Input diketik dalam satuan terpilih; dikonversi ke PCS sebelum divalidasi
    // terhadap stok (available_stock dari server selalu PCS).
    const qty = toPcsQty(draftQty)
    if (!qty || qty < 1) {
      toast.error("Qty produk harus lebih besar dari 0")
      return
    }

    if (qty > Number(draftProduct.available_stock || 0)) {
      toast.error(`Qty ${draftProduct.product_unit_name} melebihi stok tersedia`)
      return
    }

    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => String(item.product_unit_id) === String(draftProduct.product_unit_id)
      )

      if (existingIndex >= 0) {
        const next = [...prev]
        const nextQty = Number(next[existingIndex].qty || 0) + qty
        if (nextQty > Number(next[existingIndex].available_stock || 0)) {
          toast.error(`Total qty ${draftProduct.product_unit_name} melebihi stok tersedia`)
          return prev
        }

        next[existingIndex] = {
          ...next[existingIndex],
          qty: nextQty,
        }
        return next
      }

      return [
        ...prev,
        {
          line_key: `${draftProduct.product_unit_id}-${Date.now()}`,
          ...draftProduct,
          qty,
        },
      ]
    })

    setDraftProduct(null)
    setDraftQty("")
  }

  const updateItemQty = (lineKey, rawQty) => {
    const qty = toPcsQty(rawQty)

    setItems((prev) =>
      prev.map((item) => {
        if (item.line_key !== lineKey) {
          return item
        }

        const cappedQty = qty > Number(item.available_stock || 0)
          ? Number(item.available_stock || 0)
          : qty

        return {
          ...item,
          qty: cappedQty < 0 ? 0 : cappedQty,
        }
      })
    )
  }

  const removeItem = (lineKey) => {
    setItems((prev) => prev.filter((item) => item.line_key !== lineKey))
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (nProgress.isStarted()) {
      toast.error("Aksi sebelumnya sedang diproses")
      return
    }

    if (!fromOutletId || !toOutletId) {
      toast.error("Lengkapi semua field yang wajib")
      return
    }

    if (String(fromOutletId) === String(toOutletId)) {
      toast.error("Outlet asal dan tujuan tidak boleh sama")
      return
    }

    if (!items.length) {
      toast.error("Minimal satu produk harus dipilih")
      return
    }

    const invalidQty = items.find(
      (item) => Number(item.qty || 0) < 1 || Number(item.qty || 0) > Number(item.available_stock || 0)
    )

    if (invalidQty) {
      toast.error(`Qty ${invalidQty.product_unit_name} tidak valid`)
      return
    }

    onSubmit({
      from_outlet_id: fromOutletId,
      to_outlet_id: toOutletId,
      from_outlet_name: findBranchNameById(fromOutletId),
      to_outlet_name: findBranchNameById(toOutletId),
      delivery_order_no: deliveryOrderNo.trim(),
      items: items.map((item) => ({
        product_unit_id: item.product_unit_id,
        qty: Number(item.qty),
      })),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-8">
      <div className="mt-10 sm:mt-0">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">{lang.main_form}</h3>
              <p className="mt-1 text-sm text-gray-600">{lang.please_fill_the_provided_form_input}</p>
            </div>
          </div>

          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 sm:p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="from_outlet_id" value={`Outlet Asal * ${loadingBranches ? '(memuat...)' : ''}`} />
                    <select
                      id="from_outlet_id"
                      className="w-full h-10 px-3 border rounded-xl bg-transparent disabled:bg-neutral-100"
                      value={fromOutletId}
                      onChange={(event) => setFromOutletId(event.target.value)}
                      disabled={isBranchLocked}
                    >
                      <option value="">— Pilih Outlet Asal —</option>
                      {branches
                        .filter((branch) => !isBranchLocked || String(branchOptionValue(branch)) === String(currentBranch.id))
                        .map((branch) => {
                          const value = branchOptionValue(branch)
                          return (
                            <option key={`from-${value}`} value={value}>
                              {branchOptionLabel(branch)}
                            </option>
                          )
                        })}
                    </select>
                    <ErrorMessage error={errors.from_outlet_id} />
                  </div>

                  <div>
                    <Label htmlFor="to_outlet_id" value="Outlet Tujuan *" />
                    <select
                      id="to_outlet_id"
                      className="w-full h-10 px-3 border rounded-xl bg-transparent"
                      value={toOutletId}
                      onChange={(event) => setToOutletId(event.target.value)}
                    >
                      <option value="">— Pilih Outlet Tujuan —</option>
                      {branches
                        .filter((branch) => String(branchOptionValue(branch)) !== String(fromOutletId))
                        .map((branch) => {
                          const value = branchOptionValue(branch)
                          return (
                            <option key={`to-${value}`} value={value}>
                              {branchOptionLabel(branch)}
                            </option>
                          )
                        })}
                    </select>
                    <ErrorMessage error={errors.to_outlet_id} />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="delivery_order_no" value="No. Delivery Order" />
                    <Input
                      id="delivery_order_no"
                      value={deliveryOrderNo}
                      onChange={(event) => setDeliveryOrderNo(event.target.value)}
                      placeholder="Kosongkan untuk auto-generate"
                      error={errors.delivery_order_no}
                    />
                    <Description value="Kosongkan jika ingin nomor DO dibuat otomatis saat draft disimpan." />
                  </div>
                </div>

                <div className="border rounded-xl p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label value="Pilih Produk *" />
                      <AsyncProductUnitPicker
                        disabled={!fromOutletId}
                        value={draftProduct}
                        outlet_group_id={data?.outlet_group_id}
                        outlet_id={fromOutletId}
                        onChange={setDraftProduct}
                      />
                    </div>

                    <div className="space-y-4">
                      {/* 290726: satuan GLOBAL seluruh item transfer. Qty tetap
                          dikirim ke server dalam PCS. */}
                      <div>
                        <Label htmlFor="transfer_uom" value="Satuan (semua item)" />
                        <select
                          id="transfer_uom"
                          value={globalUom}
                          onChange={(event) => setGlobalUom(event.target.value)}
                          className="w-full px-3 py-2 text-sm border rounded-lg border-neutral-200 focus:outline-none focus:ring focus:ring-neutral-200"
                        >
                          {((satuanOptions ?? UOM_FALLBACK).includes(globalUom)
                            ? (satuanOptions ?? UOM_FALLBACK)
                            : [globalUom, ...(satuanOptions ?? UOM_FALLBACK)]
                          ).map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="draft_qty" value={`Qty (${globalUom}) *`} />
                        <Input
                          id="draft_qty"
                          type="number"
                          min={0}
                          step="any"
                          value={draftQty}
                          onChange={(event) => setDraftQty(event.target.value)}
                          placeholder="0"
                        />
                        {draftProduct && (
                          <Description
                            value={`Stok tersedia: ${toUomQty(draftProduct.available_stock)} ${globalUom}`
                              + (masterUomFactor(globalUom) > 1
                                  ? ` (${Number(draftProduct.available_stock || 0).toLocaleString("id-ID")} PCS)`
                                  : "")}
                          />
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={addDraftProduct}
                        className="px-4 py-2 text-sm font-medium text-white bg-brand-900 rounded-xl"
                      >
                        Tambah Produk
                      </button>
                    </div>
                  </div>

                  <div className="overflow-hidden border rounded-xl">
                    <table className="min-w-full text-sm">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Produk</th>
                          <th className="px-3 py-2 text-left">SKU</th>
                          <th className="px-3 py-2 text-right">Stok ({globalUom})</th>
                          <th className="px-3 py-2 text-right">Qty ({globalUom}) *</th>
                          <th className="px-3 py-2 text-center">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-neutral-500">
                              Belum ada produk dipilih.
                            </td>
                          </tr>
                        )}

                        {items.map((item) => (
                          <tr key={item.line_key} className="border-t hover:bg-brand-50 transition-colors">
                            <td className="px-3 py-3">
                              <div className="font-medium text-neutral-900">{item.product_unit_name}</div>
                              <div className="text-xs text-neutral-500">{item.brand}</div>
                            </td>
                            <td className="px-3 py-3 text-neutral-600">{item.sku}</td>
                            <td className="px-3 py-3 text-right text-green-700 font-medium">
                              {toUomQty(item.available_stock)}
                            </td>
                            <td className="px-3 py-3">
                              <input
                                type="number"
                                min={0}
                                step="any"
                                value={toUomQty(item.qty)}
                                onChange={(event) => updateItemQty(item.line_key, event.target.value)}
                                className="w-28 px-3 py-2 text-right border rounded-lg"
                              />
                            </td>
                            <td className="px-3 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => removeItem(item.line_key)}
                                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
                              >
                                Hapus
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {items.length > 0 && (
                        <tfoot className="bg-neutral-50 border-t">
                          <tr>
                            <td colSpan={3} className="px-3 py-2 text-right font-semibold">Total Qty</td>
                            <td className="px-3 py-2 text-right font-semibold">{totalQty.toLocaleString("id-ID")}</td>
                            <td />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>

                  <ErrorMessage error={errors.items} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-xs text-right">
        <button
          type="submit"
          className="items-center px-6 py-3 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
        >
          <span>{lang.create}</span>
        </button>
      </div>
    </form>
  )
}
